// @flow
import React from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';
import {usePrefs} from 'docs/data';
import type {
  ClientsForPlatform,
  Client,
  Platform,
} from 'docs/data/integration-builder';
import {getPlatform} from 'docs/util';
import {Setting} from './Setting';

const messages = defineMessages({
  frontend: {
    id: 'docsApp.integrationBuilder.settings.frontend',
    description: 'Frontend label',
    defaultMessage: 'Frontend:',
  },
});

type ClientSettingProps = {|
  clients: ClientsForPlatform,
|};

export const ClientSetting = ({clients}: ClientSettingProps) => {
  const {getPref, setPrefs} = usePrefs();
  const clientPref = getPref('client');
  const platformPref = getPref('platform');

  let client: Client;
  let platform: ?Platform;
  let availableClients: Array<Client> = [];
  const platforms: Array<Platform> = Object.keys(clients);

  platform =
    platformPref && platforms.includes(platformPref) ? platformPref : null;

  if (platform) {
    // The user has previously selected a platform so default with a client from that platform setting
    if (clientPref && clients[platform].includes(clientPref)) {
      client = clientPref;
    } else {
      client = clients[platform][0];
      // Need to set prefs correctly whenever we are defaulting because a client pref is not defined
      setPrefs({client});
    }
  } else if (clientPref) {
    // The user has previously selected a client so default with a client and platform from that client setting
    platform = getPlatform(clients, clientPref);
    client = clientPref;

    if (!platform) {
      // Previously selected client setting is not an option in this guide, default to first available
      platform = platforms[0];
      client = clients[platform][0];
      setPrefs({platform, client});
    }
  } else {
    // Previously selected platform and client settings are not an option in this guide, default to first available
    platform = platforms[0];
    client = clients[platform][0];
    setPrefs({platform, client});
  }

  availableClients = clients[platform];

  return (
    <Setting
      label={<FormattedMessage {...messages.frontend} />}
      pref="client"
      activeItem={client}
      items={availableClients}
      getExtraPrefs={(selectedClient) => {
        let newPlatform = getPlatform(clients, selectedClient);
        if (!newPlatform) {
          // We should never get here because that means we managed to select a platform not in the client list
          // but this makes Flow happy
          newPlatform = platforms[0];
        }
        return {
          platform: newPlatform,
        };
      }}
    />
  );
};
